import React from "react";
import { Link } from "react-router-dom";
import { Radio, ArrowLeft, Headphones, Globe2, Coins, Sparkles } from "lucide-react";

export default function About() {
  return (
    <main className="max-w-3xl mx-auto px-5 py-16" data-testid="about-page">
      <Link to="/" className="inline-flex items-center gap-2 fl-mono text-[11px] tracking-widest uppercase text-slate-400 hover:text-emerald-500 mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Fliboe
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <Radio className="w-6 h-6 text-emerald-500" />
        <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-emerald-500">// The Company</div>
      </div>
      <h1 className="fl-display text-4xl sm:text-5xl font-extrabold tracking-tighter">About Fliboe FM</h1>
      <p className="text-slate-400 mt-3 text-sm">Independent internet radio, built for creators.</p>

      <div className="mt-10 space-y-8 text-slate-300 text-[15px] leading-relaxed">
        <section>
          <p>
            Fliboe FM is an independent internet radio platform that lets anyone — musicians, producers, hosts, and
            small brands — launch their own worldwide 24/7 station in minutes. No labels. No gatekeepers. No 30% cut
            on tips. We built the platform we wished existed when we were creators looking for a way in.
          </p>
        </section>

        <section>
          <h2 className="text-white font-bold text-lg mb-2">Our mission</h2>
          <p>
            Give every artist, host, and small brand a real radio distribution channel that pays them, not us.
            Fliboe combines auto-DJ playback, AI voice hosts, listener demographics, embeddable players, and a native
            SEO blog (Fliboe Pulse) into one platform — so creators can build an audience and monetize on their own terms.
          </p>
        </section>

        <section>
          <h2 className="text-white font-bold text-lg mb-2">What makes us different</h2>
          <ul className="space-y-3">
            <li className="flex gap-3">
              <Headphones className="w-4 h-4 mt-1 shrink-0 text-emerald-500" />
              <span><strong className="text-white">Sync worldwide.</strong> Every listener across the planet hears the same second of the same track — a real live radio experience, not a personalized playlist.</span>
            </li>
            <li className="flex gap-3">
              <Sparkles className="w-4 h-4 mt-1 shrink-0 text-emerald-500" />
              <span><strong className="text-white">AI DJ between every song.</strong> Fliboe generates natural voice-over drops that introduce your tracks, plug your station, and keep the vibe alive — no human host required.</span>
            </li>
            <li className="flex gap-3">
              <Coins className="w-4 h-4 mt-1 shrink-0 text-emerald-500" />
              <span><strong className="text-white">0% commission on tips.</strong> Every dollar a listener sends lands directly in the creator&apos;s PayPal, Cash App, or Venmo. We take nothing.</span>
            </li>
            <li className="flex gap-3">
              <Globe2 className="w-4 h-4 mt-1 shrink-0 text-emerald-500" />
              <span><strong className="text-white">Global from day one.</strong> Your station is instantly reachable from any country, on any device, with an embeddable player for your website and socials.</span>
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-white font-bold text-lg mb-2">Fliboe Pulse — our editorial arm</h2>
          <p>
            Alongside stations, Fliboe operates <Link to="/pulse" className="text-emerald-400 hover:underline">Fliboe Pulse</Link>,
            a music, culture, and creator-economy publication. Every Pulse post is written to give real value to music
            listeners — spotlighting artists, breaking down genres, and guiding fans to independent stations they&apos;d
            never find on the major platforms.
          </p>
        </section>

        <section>
          <h2 className="text-white font-bold text-lg mb-2">Ownership &amp; contact</h2>
          <p>
            Fliboe FM is an independently owned platform, self-funded, and operated by its founder. We do not sell user
            data, we do not run pay-to-play, and we don&apos;t take equity from creators. For questions, partnerships,
            or press, reach us at <Link to="/contact" className="text-emerald-400 hover:underline">the contact page</Link>.
          </p>
        </section>

        <section className="pt-6 border-t border-white/5 text-slate-400 text-sm">
          <p>
            Fliboe FM · An independent internet radio platform · Made for creators who&apos;d rather build than beg.
          </p>
        </section>
      </div>
    </main>
  );
}
